<template>
  <component
    :is="type"
    :href="href"
    :type="submit ? 'submit' : 'button'"
    :title="title"
    :disabled="disabled"
    :class="[
      'button',
      'button--' + size,
      state ? 'button--' + state : '',
      light ? 'button--light' : '',
      'button--' + variation,
      ghost ? 'button--ghost' : '',
      iconButton ? 'button--icon-button' : '',
    ]"
    @click="onClick()"
  >
    <Icon v-if="variation == 'loading'" name="loading" aria-label="Loading" />
    <slot />
  </component>
</template>
<script>
/**
 * Buttons are generally used for interface actions. Suitable for all-purpose use.
 * Defaults to appearance that has white background with iLOQ light blue.
 */

import Icon from "@/design-system/elements/Icon.vue";

export default {
  name: "Button",
  components: {
    Icon,
  },
  status: "prototype",
  release: "3.5.0",
  props: {
    /**
     * The html element used for the button: `button, a`.
     * Use a link when you’re navigating to another location.
     * Use button when you are performing an action on the same view
     *
     */
    type: {
      type: String,
      default: "button",
      validator: (value) => {
        return value.match(/(button|a)/);
      },
    },
    /**
     * The size of the button. Defaults to medium.
     * `small, medium, large`
     */
    size: {
      type: String,
      default: "medium",
      validator: (value) => {
        return value.match(/(small|medium|large)/);
      },
    },
    /**
     * When setting the button’s type to a link, use this option to give a href.
     */
    href: {
      type: String,
      default: null,
    },
    /**
     * Icon Button, set this option to true to fix paddings for icon
     */
    iconButton: {
      type: [Boolean, String],
      default: false,
    },
    /**
     * Set the button’s type to “submit” by providing "submit" or "true".
     */
    submit: {
      type: String,
      default: null,
      validator: (value) => {
        return value.match(/(null|submit|true)/);
      },
    },
    /**
     * Manually trigger various states of the button.
     * `hover, active, focus`
     */
    state: {
      type: String,
      default: null,
      validator: (value) => {
        return value.match(/(hover|active|focus)/);
      },
    },

    /**
     * Ghost button style (Default state without background)
     *
     */
    ghost: {
      type: Boolean,
      default: false,
    },

    /**
     * Light button style (narrower borders etc.)
     *
     */
    light: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Button title attribute, should be used together with icon buttons
     *
     */
    title: {
      type: String,
      required: false,
      default: "",
    },

    /**
     * Style variation to give additional meaning.
     * ´primary, default, secondary, info, warning, danger, success, confirm, cancel, delete, loading´
     */
    variation: {
      type: String,
      default: null,
      validator: (value) => {
        return value.match(
          /(primary|default|secondary|info|warning|danger|success|confirm|cancel|delete|loading)/
        );
      },
    },
  },
  methods: {
    onClick(value) {
      this.$emit("onClick", value);
    },
  },
};
</script>

<style lang="scss">
.button {
  font-size: 14px;
  max-height: 40px;
  text-transform: none;
  white-space: nowrap;
}
</style>
